import React, { FunctionComponent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { PetAndClinicInfo, SurveyFull } from '@companion-professional/dctypes';
import {
  Badge,
  DynamicForm,
  DynamicFormProps,
  DynamicFormQuestionField,
  Heading,
  IconTextButton,
  QueryLoaderWrapper,
  Text
} from '@companion-professional/components';
import { useSurvey } from '../../hooks/useSurvey';
import { Section } from '../Section';
import { useCheckupsForPetWithoutSurveyAnswers } from '../../hooks/useCheckupsForPetWithoutSurveyAnswers';
import { AnsweredBySelector } from '../AnsweredBySelector';
import { SurveyCheckupCardsSection } from './SurveyCheckupCardsSection';

interface SurveyDisplayProps extends DynamicFormProps {
  // surveyId is the id of the survey to display.
  surveyId: string;

  // pet is the pet that the survey is for
  pet: PetAndClinicInfo;

  // checkupRequestIds is a list of checkup request ids that are associated with this survey.
  checkupRequestIds?: string[];

  // answersSurveyVersion is the version of the survey that the answers are for.  If provided, that version of the
  // survey will be loaded.  If not provided, the latest version of the survey will be retrieved.
  answersSurveyVersion?: number;

  // surveySaved is a flag to indicate that the survey has been saved.  If true, a success message will be displayed.
  surveySaved?: boolean;

  // answeredByUserId is the id of the user that answered the survey.
  answeredByUserId?: string;

  // onSurveySubmit is a function that is called when the survey is submitted.  It is passed the form data and the
  // loaded survey.
  onSurveySubmit?: (
    formData: Record<string, unknown>,
    survey: SurveyFull<DynamicFormQuestionField>,
    answeredByUserId: string,
    checkupsToAdd?: string[]
  ) => void;
}

// SurveyDisplay displays a survey with the given surveyId.  It also takes in all the props for DynamicForm and passes
// them into that component (so you can pass in extra parameters like answers, showSubmitButton, etc).
export const SurveyDisplay: FunctionComponent<SurveyDisplayProps> = ({
  surveyId,
  pet,
  checkupRequestIds = [],
  surveySaved = false,
  onSurveySubmit = () => {},
  answersSurveyVersion,
  answeredByUserId,
  ...props
}) => {
  const navigate = useNavigate();
  const {
    checkups = [],
    isPending: checkupsIsPending,
    error: checkupsError
  } = useCheckupsForPetWithoutSurveyAnswers(pet.id, surveyId);
  const [checkupsToAddIds, setCheckupsToAddIds] = useState<string[]>([]);
  const [userId, setUserId] = useState<string | undefined>(answeredByUserId);
  const [answeredByValidationError, setAnsweredByValidationError] = useState<string | undefined>();

  // This fetches a survey with the given surveyId and version of the answers (if provided).  In the future (and if we
  // want to add the feature), we can use this to assign the answers to a different version of the survey.
  // New answers will not have an answersSurveyVersion, so they will be assigned to the latest version of the survey.
  const { survey, error, isPending } = useSurvey(surveyId, answersSurveyVersion);

  const surveyTitle = useMemo(
    () => (survey?.title && survey.title.length > 0 ? survey.title : survey?.name),
    [survey?.title, survey?.name]
  );

  // isNewSurvey is a flag to indicate if the survey is new (i.e. no answers have been provided yet).
  const isNewSurvey = props.answers === undefined;

  // unattachedCheckupIds is a list of checkup request ids that are not currently attached to any survey.  These
  // can be added to this survey by answering the "yes" to questions in the AddCheckupToSurveyWizard.
  const unattachedCheckupIds = useMemo(() => {
    return checkups.filter((checkup) => !checkupRequestIds.includes(checkup.id)).map((checkup) => checkup.id);
  }, [checkups, checkupRequestIds]);

  return (
    <QueryLoaderWrapper isPending={isPending && checkupsIsPending} error={error && checkupsError}>
      <div className="my-6">
        <div className="flex flex-row items-start justify-between gap-x-1">
          <Heading level="section">{surveyTitle}</Heading>
          <Badge variant="secondary">v{survey?.version}</Badge>
        </div>
        <Text tone="muted">{survey?.description}</Text>
      </div>
      <div className="space-y-4">
        {surveySaved ? (
          <Section>
            <div className="flex flex-col items-center p-4">
              <Heading level="subsection" as="p" className="mb-4">
                {surveyTitle} has been saved.
              </Heading>
              <CheckCircle2 size={48} className="text-success" />
            </div>
          </Section>
        ) : (
          <>
            <Section>
              <AnsweredBySelector
                answeredByUserId={userId || ''}
                onAnsweredByUserIdChange={(userId) => {
                  setUserId(userId);
                  setAnsweredByValidationError(undefined);
                }}
                originalAnsweredByUserId={answeredByUserId}
                validationError={answeredByValidationError}
              />
            </Section>
            <SurveyCheckupCardsSection
              surveyId={surveyId}
              isNewSurvey={isNewSurvey}
              preSelectedCheckupIds={checkupRequestIds}
              unattachedCheckupIds={unattachedCheckupIds}
              toBeAttachedCheckupIds={checkupsToAddIds}
              setCheckupsToAddIds={setCheckupsToAddIds}
            />
            <Section className="py-4">
              <DynamicForm
                showNumbers
                fields={survey?.questions || []}
                {...props}
                onSubmit={(formData) => {
                  if (!userId || userId.length === 0) {
                    setAnsweredByValidationError('A user must be selected to answer the survey.');
                    return;
                  }
                  if (survey) {
                    onSurveySubmit(formData, survey, userId, checkupsToAddIds);
                  }
                }}
              />
            </Section>
          </>
        )}
        <div>
          <IconTextButton
            size="small"
            variant="secondary"
            onClick={() => {
              navigate(`/pets/${pet.id}`);
            }}
            Icon={ArrowLeft}
          >
            Back To {pet.name}
          </IconTextButton>
        </div>
      </div>
    </QueryLoaderWrapper>
  );
};
